<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { PDFtoIMG, PDFtoText } from "@/common/services/convert";

import {
  analysisResultList,
  analysisResultCreate,
  analysisResultPatch,
  analysisResultDelete,
  analysisResultResume,
} from "@/common/api/rest/analysis/result";
import { fileCreate, fileDelete } from "@/common/api/rest/file/file";
import { analysisMethodList } from "@/common/api/rest/analysis/method";
import { analysisSampleList } from "@/common/api/rest/analysis/sample";
import { evaluationLaboratoryList } from "@/common/api/rest/evaluation/laboratory";

import type { AnalysisType } from "@/common/types/analysis";
import type { ResultType } from "@/common/types/result";
import type { FileType, FieldType } from "@/common/types/common";
import type { SampleListType } from "@/common/types/sample";
import type { MethodItemType } from "@/common/types/method/method-item";
import type { LaboratoryListType } from "@/common/types/laboratory";

import { fileUploadForm, resultFieldForm, viewImage } from "@/components";
import {
  baseConfirm,
  baseDate,
  baseInput,
  baseLoading,
  baseModal,
  baseTextarea,
  baseSelect,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: AnalysisType | null;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);

const fileExtensions = [".jpg", ".jpeg", ".pdf"];

const fileKey = ref(0);

const results = ref<ResultType[]>([]);
const fields = ref<FieldType[][]>([]);
const fieldsAi = ref<FieldType[][]>([]);
const fieldPayload = ref<FieldType | null>(null);
const loadingResult = ref<string[]>([]);
const laboratories = ref<LaboratoryListType[]>([]);

const methods = ref<MethodItemType[]>([]);
const samples = ref<SampleListType[]>([]);

const selected = ref<ResultType | null>(null);
const selectedFile = ref<string | null>(null);
const selectedImage = ref<string | null>(null);
const selectedField = ref<{
  index: number;
  field?: number;
  type: "fields" | "fields_ai";
} | null>(null);

const showDelResult = ref(false);
const showDelImage = ref(false);
const showAddField = ref(false);
const showDelField = ref(false);
const fieldEdit = ref(false);

const methodOptions = computed(() =>
  methods.value.map((item) => ({ option: item.code, value: item.method_id })),
);

const sampleOptions = computed(() =>
  samples.value.map((item) => ({ option: item.code, value: item.sample_id })),
);

const laboratoryOptions = computed(() =>
  laboratories.value.map((item) => ({
    option: item.name,
    value: item.laboratory_id,
  })),
);

const laboratoryStatusOptions = computed(() => [
  { option: "Pendente", value: "PENDING" },
  { option: "Enviado", value: "SENT" },
  { option: "Recebido", value: "RECEIVED" },
  { option: "Cancelado", value: "CANCELED" },
]);

function resumeMetadata() {
  fields.value = results.value.map((item) => item.metadata?.fields || []);
  fieldsAi.value = results.value.map((item) => item.metadata?.fields_ai || []);
}

function fecthData() {
  if (!props.data) return;

  analysisResultList(props.data.account_id, {
    analysis_id: props.data.analysis_id,
    full: true,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        results.value = data.list?.map((item) => ({
          ...item,
          metadata:
            typeof item.metadata === "string"
              ? JSON.parse(item.metadata)
              : item.metadata,
        }));
        resumeMetadata();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchMethods() {
  if (!props.data) return;

  analysisMethodList(props.data.account_id, {
    analysis_id: props.data.analysis_id,
    full: true,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        methods.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchSamples() {
  if (!props.data?.analysis_id) {
    return;
  }

  analysisSampleList(props.data.account_id, {
    analysis_id: props.data.analysis_id,
    full: true,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        samples.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchLaboratories() {
  if (!props.data?.evaluation_id) {
    return;
  }

  evaluationLaboratoryList(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    page: 0,
    full: true,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        laboratories.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchData(result_id: string, payload: Partial<ResultType>) {
  analysisResultPatch(props.data.account_id, result_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fecthData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateResult(index: number, key: keyof ResultType) {
  const result = results.value[index];

  patchData(result.result_id, { [key]: result[key] });
}

function updateMetadata(index: number, key: "fields" | "fields_ai") {
  const result = results.value[index];

  const data = {
    fields: fields.value,
    fields_ai: fieldsAi.value,
  };

  const metadataString = JSON.stringify({
    ...result.metadata,
    [key]: data[key][index],
  });

  patchData(result.result_id, { metadata: metadataString });
}

function addResult() {
  if (!props.data) return;

  store.commit("SET_LOADING", true);

  analysisResultCreate(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    analysis_id: props.data.analysis_id,
  })
    .then((response) => {
      const { success, message } = response;

      fecthData();
      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function delResult(index: number) {
  selected.value = results.value[index];
  showDelResult.value = true;
}

function delResultCancel() {
  selected.value = null;
  showDelResult.value = false;
}

function delResultConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);
  showDelResult.value = false;

  analysisResultDelete(props.data.account_id, selected.value.result_id)
    .then((response) => {
      const { success, message } = response;

      fecthData();
      delResultCancel();
      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

// ADD IMAGE
async function uploadFile(index: number, fileData: FileType) {
  const { extension, name, file } = fileData;

  store.commit("SET_LOADING", true);

  const result = results.value[index];

  if (extension?.toLowerCase() === "pdf") {
    const pdfText = await PDFtoText(file);

    if (pdfText.success) {
      const texts = pdfText.pages?.map((item) => item.text).join("\n");

      const metadataString = JSON.stringify({
        ...result.metadata,
        pdf_text: texts,
      });

      patchData(result.result_id, { metadata: metadataString });
    }

    const converted = await PDFtoIMG(file);

    if (converted.success) {
      for (const item of converted.pages ?? []) {
        await fileCreate(props.data.account_id, result.result_id, {
          file: item.image.split(",")[1],
          type: "RESULT",
          name,
          extension: "jpg",
          page: item.page,
          infos: item.image.split(",")[0],
        })
          .then((response) => {
            if (!response.success) {
              toast({ message: response.message, type: "error" });
            }
          })
          .catch(() => {
            toast({ message: t("error.default"), type: "error" });
          });
      }
    } else {
      toast({ message: t("error.default"), type: "error" });
    }

    fileKey.value++;
    fecthData();
    store.commit("SET_LOADING", false);
  } else {
    fileCreate(props.data.account_id, result.result_id, {
      ...fileData,
      type: "RESULT",
    })
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fecthData();
          toast({ message: message, type: "success" });
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      })
      .finally(() => {
        fileKey.value++;
        store.commit("SET_LOADING", false);
      });
  }
}

function delImage(index: number, file_id?: string) {
  if (!file_id) return;

  selected.value = results.value[index];
  selectedFile.value = file_id;
  showDelImage.value = true;
}

function delImageCancel() {
  selected.value = null;
  selectedFile.value = null;
  showDelImage.value = false;
}

function delImageConfirm() {
  if (!selected.value || !selectedFile.value) return;

  showDelImage.value = false;
  store.commit("SET_LOADING", true);

  fileDelete(props.data.account_id, selectedFile.value)
    .then((response) => {
      const { success, message } = response;

      fecthData();
      delImageCancel();
      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

// FIELD
function addField(index: number) {
  showAddField.value = true;
  selectedField.value = { index: index, type: "fields" };
}

function addFieldCancel() {
  selected.value = null;
  fieldPayload.value = null;
  showAddField.value = false;
  fieldEdit.value = false;
}

function addFieldConfirm() {
  if (!selectedField.value || !fieldPayload.value) return;

  if (!fields.value[selectedField.value.index])
    fields.value[selectedField.value.index] = [];

  fields.value[selectedField.value.index].push(fieldPayload.value);

  updateMetadata(selectedField.value.index, "fields");
  addFieldCancel();
}

function editField(
  index: number,
  field_index: number,
  type: "fields" | "fields_ai",
) {
  const data = {
    fields: fields.value,
    fields_ai: fieldsAi.value,
  };

  fieldEdit.value = true;
  selectedField.value = { index, field: field_index, type };
  fieldPayload.value = data[type][index][field_index];
  showAddField.value = true;
}

function editFieldConfirm() {
  if (
    !selectedField.value ||
    !fieldPayload.value ||
    selectedField.value.field === undefined
  )
    return;

  const { index, field: field_index, type } = selectedField.value;

  if (type === "fields") {
    fields.value[index][field_index] = fieldPayload.value;
  }

  if (type === "fields_ai") {
    fieldsAi.value[index][field_index] = fieldPayload.value;
  }

  updateMetadata(selectedField.value.index, type);
  addFieldCancel();
}

function submitField() {
  if (!fieldEdit.value) addFieldConfirm();
  else editFieldConfirm();
}

function delField(
  index: number,
  field_index: number,
  type: "fields" | "fields_ai",
) {
  selectedField.value = { index, field: field_index, type };
  showDelField.value = true;
}

function delFieldCancel() {
  selectedField.value = null;
  showDelField.value = false;
}

function delFieldConfirm() {
  if (!selectedField.value) return;

  const { index, field: field_index, type } = selectedField.value;

  if (type === "fields") {
    fields.value[index].splice(field_index!, 1);
  }

  if (type === "fields_ai") {
    fieldsAi.value[index].splice(field_index!, 1);
  }

  updateMetadata(selectedField.value.index, type);
  delFieldCancel();
}

async function generateResume(item: any) {
  store.commit("SET_LOADING", true);

  console.log("result", item);

  await analysisResultResume(props.data.account_id, item.result_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fecthData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function resultLaboratoryChange(index: number) {
  patchData(results.value[index].result_id, {
    laboratory_id: results.value[index].laboratory_id,
    laboratory_description: null,
    laboratory_receive_date: null,
    laboratory_send_date: null,
    laboratory_status: null,
  });
}

const debounceUpdate = debounce(updateResult, 1000);
const debounceMetadata = debounce(updateMetadata, 1000);

onMounted(() => {
  fecthData();
  fetchMethods();
  fetchSamples();
  fetchLaboratories();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <div class="content">
      <template v-if="results.length === 0">
        <div class="box-empty">
          {{ t("result.texts.result_empty") }}
          <button class="btn btn-md btn-primary uppercase" @click="addResult()">
            {{ t("result.labels.add_result") }}
          </button>
        </div>
      </template>

      <template v-else>
        <div class="results">
          <template v-for="(item, index) in results" :key="item.result_id">
            <div class="item">
              <div class="item-content">
                <div
                  class="files"
                  :class="{
                    'has-images': item.files,
                  }"
                >
                  <templete v-if="item.files">
                    <div class="images">
                      <template
                        v-for="(image, img_idx) in item.files"
                        :key="img_idx"
                      >
                        <div class="image">
                          <img
                            :src="image.file"
                            :alt="`image ${img_idx + 1}`"
                            @click="selectedImage = image.file"
                          />
                          <div class="actions">
                            <button
                              class="btn btn-sm btn-default"
                              @click="delImage(index, image.file_id)"
                            >
                              <svg-icon icon="trash" size="1.25rem" />
                            </button>
                          </div>
                        </div>
                      </template>
                    </div>
                  </templete>

                  <template v-if="!item.files?.length">
                    <div class="upload">
                      <file-upload-form
                        :key="fileKey"
                        :extensions="fileExtensions"
                        @update-data="uploadFile(index, $event)"
                      />
                    </div>
                  </template>
                </div>

                <div class="form">
                  <base-input
                    v-model="item.name"
                    :value="item.name"
                    required
                    @input="debounceUpdate(index, 'name')"
                  >
                    <template #label> {{ t("common.labels.name") }} *</template>
                  </base-input>

                  <base-select
                    v-model="item.method_id"
                    :value="item.method_id"
                    :options="methodOptions"
                    :is-empty="true"
                    @change="debounceUpdate(index, 'method_id')"
                  >
                    <template #label>
                      {{ t("common.labels.method") }}
                    </template>
                  </base-select>

                  <base-select
                    v-model="item.sample_id"
                    :value="item.sample_id"
                    :options="sampleOptions"
                    :is-empty="true"
                    @change="debounceUpdate(index, 'sample_id')"
                  >
                    <template #label>
                      {{ t("common.labels.sample") }}
                    </template>
                  </base-select>

                  <base-select
                    v-model="item.laboratory_id"
                    :value="item.laboratory_id"
                    :options="laboratoryOptions"
                    :is-empty="true"
                    @change="resultLaboratoryChange(index)"
                  >
                    <template #label>
                      {{ t("common.labels.laboratory") }}
                    </template>
                  </base-select>

                  <template v-if="item.laboratory_id">
                    <div class="laboratory-group">
                      <base-select
                        v-model="item.laboratory_status"
                        :value="item.laboratory_status"
                        :options="laboratoryStatusOptions"
                        :is-empty="false"
                        @change="debounceUpdate(index, 'laboratory_status')"
                      >
                        <template #label>
                          Status do Envio para Laboratório
                        </template>
                      </base-select>

                      <div class="group-row">
                        <base-date
                          v-model="item.laboratory_send_date"
                          :value="item.laboratory_send_date"
                          :required="false"
                          value-mask="YYYY-MM-DD"
                          placeholder="--/--/----"
                          @update:model-value="
                            debounceUpdate(index, 'laboratory_send_date')
                          "
                        >
                          <template #label>
                            Data de envio para laboratório
                          </template>
                        </base-date>

                        <base-date
                          v-model="item.laboratory_receive_date"
                          :value="item.laboratory_receive_date"
                          :required="false"
                          value-mask="YYYY-MM-DD"
                          placeholder="--/--/----"
                          @update:model-value="
                            debounceUpdate(index, 'laboratory_receive_date')
                          "
                        >
                          <template #label>
                            Data de recepção do laboratório
                          </template>
                        </base-date>
                      </div>

                      <base-textarea
                        v-model="item.laboratory_description"
                        :value="item.laboratory_description"
                        :placeholder="t('common.placeholders.describe')"
                        @input="debounceUpdate(index, 'laboratory_description')"
                      >
                        <template #label>
                          {{ t("common.labels.description") }}
                        </template>
                      </base-textarea>
                    </div>
                  </template>

                  <base-textarea
                    v-model="item.description"
                    :value="item.description"
                    :placeholder="t('common.placeholders.describe')"
                    @input="debounceUpdate(index, 'description')"
                  >
                    <template #label>
                      {{ t("common.labels.description") }}
                    </template>
                  </base-textarea>

                  <base-textarea
                    v-model="item.result"
                    :value="item.result"
                    :placeholder="t('common.placeholders.describe')"
                    @input="debounceUpdate(index, 'result')"
                  >
                    <template #label>
                      {{ t("common.labels.result") }}
                    </template>
                  </base-textarea>

                  <template v-if="fieldsAi[index]">
                    <template
                      v-for="(field, field_index) in fieldsAi[index]"
                      :key="field.field_id"
                    >
                      <div class="flex flex-row gap-2 items-end w-full">
                        <div class="flex-1">
                          <template v-if="field.type === 'text'">
                            <base-input
                              v-model="field.answer"
                              :value="field.answer"
                              type="text"
                              @input="debounceMetadata(index, 'fields_ai')"
                            >
                              <template #label>
                                {{ field.name }}
                              </template>
                            </base-input>
                          </template>
                          <template v-if="field.type === 'number'">
                            <base-input
                              v-model="field.answer"
                              :value="field.answer"
                              type="number"
                              @input="debounceMetadata(index, 'fields_ai')"
                            >
                              <template #label>
                                {{ field.name }}
                              </template>
                            </base-input>
                          </template>
                          <template v-if="field.type === 'textarea'">
                            <base-textarea
                              v-model="field.answer"
                              :value="field.answer"
                              @input="debounceMetadata(index, 'fields_ai')"
                            >
                              <template #label>
                                {{ field.name }}
                              </template>
                            </base-textarea>
                          </template>
                        </div>

                        <button
                          class="btn btn-default w-9 h-9 p-0 mb-1"
                          :disabled="field.type === 'range'"
                          @click="editField(index, field_index, 'fields_ai')"
                        >
                          <svg-icon icon="edit" size="1.25rem" />
                        </button>

                        <button
                          class="btn btn-default w-9 h-9 p-0 mb-1"
                          @click="delField(index, field_index, 'fields_ai')"
                        >
                          <svg-icon icon="trash" size="1.25rem" />
                        </button>
                      </div>
                    </template>
                  </template>

                  <template v-if="fields[index]">
                    <template
                      v-for="(field, field_index) in fields[index]"
                      :key="field.field_id"
                    >
                      <div class="flex flex-row gap-2 items-end w-full">
                        <div class="flex-1">
                          <template v-if="field.type === 'text'">
                            <base-input
                              v-model="field.answer"
                              :value="field.answer"
                              type="text"
                              @input="debounceMetadata(index, 'fields')"
                            >
                              <template #label>
                                {{ field.name }}
                              </template>
                            </base-input>
                          </template>
                          <template v-if="field.type === 'number'">
                            <base-input
                              v-model="field.answer"
                              :value="field.answer"
                              type="number"
                              @input="debounceMetadata(index, 'fields')"
                            >
                              <template #label>
                                {{ field.name }}
                              </template>
                            </base-input>
                          </template>
                          <template v-if="field.type === 'textarea'">
                            <base-textarea
                              v-model="field.answer"
                              :value="field.answer"
                              @input="debounceMetadata(index, 'fields')"
                            >
                              <template #label>
                                {{ field.name }}
                              </template>
                            </base-textarea>
                          </template>
                        </div>

                        <button
                          class="btn btn-default w-9 h-9 p-0 mb-1"
                          :disabled="field.type === 'range'"
                          @click="editField(index, field_index, 'fields')"
                        >
                          <svg-icon icon="edit" size="1.25rem" />
                        </button>

                        <button
                          class="btn btn-default w-9 h-9 p-0 mb-1"
                          @click="delField(index, field_index, 'fields')"
                        >
                          <svg-icon icon="trash" size="1.25rem" />
                        </button>
                      </div>
                    </template>
                  </template>

                  <div class="flex justify-start w-full">
                    <button
                      class="btn btn-md btn-default uppercase"
                      @click="addField(index)"
                    >
                      <svg-icon icon="add" size="1.25rem" />
                      {{ t("common.labels.field") }}
                    </button>
                  </div>
                </div>
              </div>

              <div
                class="flex flex-row sm-flex-col md-flex-col gap-8 justify-center py-4 w-full"
              >
                <template v-if="item.metadata?.pdf_text">
                  <button
                    class="btn btn-md btn-default uppercase"
                    :disabled="loadingResult.includes(item.result_id)"
                    @click="generateResume(item)"
                  >
                    <template v-if="loadingResult.includes(item.result_id)">
                      <base-loading size="1.25rem" />
                    </template>
                    Extrair Resultado
                  </button>
                </template>

                <button
                  class="btn btn-md btn-danger uppercase"
                  @click="delResult(index)"
                >
                  <svg-icon icon="trash" size="1.5rem" />
                  {{ t("result.labels.remove_result") }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <div class="box-add">
          <button class="btn btn-md btn-primary uppercase" @click="addResult()">
            {{ t("result.labels.add_result") }}
          </button>
        </div>
      </template>
    </div>
  </section>

  <view-image :image="selectedImage" @close="selectedImage = null" />

  <base-modal
    width="80%"
    :show="showAddField"
    :show-button-confirm="true"
    :title="
      fieldEdit
        ? t('result.labels.edit_field').toUpperCase()
        : t('result.labels.add_field').toUpperCase()
    "
    :button-confirm="t('common.labels.confirm')"
    :button-close="t('common.labels.cancel')"
    @confirm="submitField()"
    @close="addFieldCancel()"
  >
    <template #content>
      <result-field-form
        :data="fieldPayload"
        @update-data="fieldPayload = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showDelResult"
    max-width="400px"
    :title="t('result.modals.titles.remove_result').toUpperCase()"
    :message="t('result.modals.messages.remove_result')"
    @cancel="delResultCancel()"
    @confirm="delResultConfirm()"
  />

  <base-confirm
    :show="showDelField"
    max-width="400px"
    :title="t('result.modals.titles.remove_field').toUpperCase()"
    :message="t('result.modals.messages.remove_field')"
    @cancel="delFieldCancel()"
    @confirm="delFieldConfirm()"
  />

  <base-confirm
    :show="showDelImage"
    max-width="400px"
    :title="t('common.modals.titles.remove_image').toUpperCase()"
    :message="t('common.modals.messages.remove_image')"
    @cancel="delImageCancel()"
    @confirm="delImageConfirm()"
  />
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-block: 2rem;
  padding-inline: 1.5rem;

  .content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .box-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1.5rem;
      padding-block: 2rem;
    }

    .results {
      display: flex;
      flex-direction: column;
      gap: 2rem;

      .item {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        padding-block-end: 2rem;
        border-block-end: 1px solid var(--border);

        &-content {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .files {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          height: fit-content;
          background: var(--white-off);
          border-radius: 0.5rem;

          &.has-images {
            padding-block: 2rem;
            justify-content: flex-start;
          }

          .images {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            justify-content: space-between;
            width: 80%;
            margin-inline: auto;
            gap: 5%;
            margin-block-end: 2rem;

            .image {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 0.5rem;

              img {
                cursor: pointer;
                width: 100%;
              }
            }
          }

          .upload {
            width: 80%;
            max-width: 80%;
            margin-inline: auto;
            background: var(--white);
          }
        }

        .form {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 1rem;
        }
      }
    }

    .box-add {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1.5rem;
      padding-block-start: 3rem;
      padding-block-end: 1rem;
    }
  }

  .group-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
  }

  .laboratory-group {
    display: flex;
    flex-direction: column;
    width: calc(100% - 2rem - 2px);
    background: var(--white-off);
    border: 1px solid var(--border);
    border-radius: 0.25rem;
    padding: 1rem;
    gap: 1.5rem;
  }
}

@media screen and (max-width: 1024px) {
  .tab-component {
    .content {
      .results {
        .item {
          .item-content {
            flex-direction: column;
          }
        }
      }
    }

    .group-row {
      flex-direction: column;
      align-items: flex-start;
    }
  }
}
</style>
